import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { useEffect, useRef, useState, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Animated,
  useWindowDimensions,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { ArrowRight, RotateCcw } from "lucide-react-native";
import type {
  NativeStackScreenProps,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { useNavigation } from "@react-navigation/native";
import type { RootStackParamList } from "../App";
import {
  GlassButton,
  InviteGlassCard,
  TextActionButton,
  FieldMessage,
  LoadingState,
} from "@louise/moon-ui";
import { GradientOrb } from "@src/components/ui/GradientOrb";
import { OTPInput } from "@src/packages/form/OTPInput/OTPInput";
import { useTranslations } from "@src/packages/i18n";
import { useStytch, useStytchUser } from "../utils/useStytch";
import { useBetaInviteValidation } from "./useBetaInviteValidation";
import { useBetaInvite } from "./BetaInviteContext";
import { semanticColors } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<RootStackParamList, "BetaInvite">;

const BG_DRIFT_DURATION = 10000;

const DARK = {
  text: semanticColors.onDark.text.primary,
  textMid: semanticColors.onDark.text.secondary,
  textFaint: semanticColors.onDark.text.tertiary,
};

export default function BetaInviteScreen({ route }: Props) {
  const t = useTranslations();
  const code = route.params?.code;
  const { width: viewportWidth } = useWindowDimensions();
  const isCompact = viewportWidth < 600;
  const rootNav =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { user } = useStytchUser();
  const stytch = useStytch();
  const { setInviteData } = useBetaInvite();

  const { inviteInfo, error: validationError } = useBetaInviteValidation(code);

  const [phase, setPhase] = useState<
    "validating" | "welcome" | "otp" | "error"
  >("validating");
  const [methodId, setMethodId] = useState("");
  const [phoneLast4, setPhoneLast4] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Background drift
  const bgPan = useRef(new Animated.Value(0)).current;
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(bgPan, {
          toValue: 1,
          duration: BG_DRIFT_DURATION,
          useNativeDriver: true,
        }),
        Animated.timing(bgPan, {
          toValue: 0,
          duration: BG_DRIFT_DURATION,
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [bgPan, reducedMotion]);

  const bgTranslateX = bgPan.interpolate({
    inputRange: [0, 1],
    outputRange: [-30, 30],
  });
  const bgTranslateY = bgPan.interpolate({
    inputRange: [0, 1],
    outputRange: [-15, 15],
  });
  const bgScale = bgPan.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1.1, 1.16, 1.1],
  });

  // Staggered entrance animations
  const orbOpacity = useRef(new Animated.Value(0)).current;
  const orbScale = useRef(new Animated.Value(1.2)).current;
  const cardOpacity = useRef(new Animated.Value(0)).current;
  const cardTranslateY = useRef(new Animated.Value(40)).current;
  const titleOpacity = useRef(new Animated.Value(0)).current;
  const titleTranslateY = useRef(new Animated.Value(16)).current;
  const bodyOpacity = useRef(new Animated.Value(0)).current;
  const bodyTranslateY = useRef(new Animated.Value(16)).current;
  const ctaOpacity = useRef(new Animated.Value(0)).current;
  const ctaTranslateY = useRef(new Animated.Value(16)).current;

  const animateEntrance = useCallback(() => {
    // 1. Orb appears first (centered, large)
    Animated.parallel([
      Animated.timing(orbOpacity, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.timing(orbScale, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();

    // 2. Card fades up after orb
    setTimeout(() => {
      Animated.parallel([
        Animated.timing(cardOpacity, {
          toValue: 1,
          duration: 700,
          useNativeDriver: true,
        }),
        Animated.timing(cardTranslateY, {
          toValue: 0,
          duration: 700,
          useNativeDriver: true,
        }),
      ]).start();
    }, 400);

    // 3. Title
    setTimeout(() => {
      Animated.parallel([
        Animated.timing(titleOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.timing(titleTranslateY, {
          toValue: 0,
          duration: 600,
          useNativeDriver: true,
        }),
      ]).start();
    }, 800);

    // 4. Body text + CTA
    setTimeout(() => {
      Animated.parallel([
        Animated.timing(bodyOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.timing(bodyTranslateY, {
          toValue: 0,
          duration: 600,
          useNativeDriver: true,
        }),
      ]).start();
    }, 1100);

    setTimeout(() => {
      Animated.parallel([
        Animated.timing(ctaOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.timing(ctaTranslateY, {
          toValue: 0,
          duration: 600,
          useNativeDriver: true,
        }),
      ]).start();
    }, 1400);
  }, [
    orbOpacity,
    orbScale,
    cardOpacity,
    cardTranslateY,
    titleOpacity,
    titleTranslateY,
    bodyOpacity,
    bodyTranslateY,
    ctaOpacity,
    ctaTranslateY,
  ]);

  // Transition to welcome once validation succeeds
  useEffect(() => {
    if (inviteInfo) {
      setInviteData({
        code: code!,
        name: inviteInfo.name,
      });
      setPhase("welcome");
      animateEntrance();
    }
  }, [inviteInfo, code, setInviteData, animateEntrance]);

  // Transition to error if validation fails
  useEffect(() => {
    if (validationError) {
      setPhase("error");
      animateEntrance();
    }
  }, [validationError, animateEntrance]);

  // No code provided
  useEffect(() => {
    if (!code) {
      setPhase("error");
      animateEntrance();
    }
  }, [code, animateEntrance]);

  // After auth, navigate to onboarding
  useEffect(() => {
    if (user && (phase === "otp" || phase === "welcome")) {
      rootNav.reset({ index: 0, routes: [{ name: "Onboarding" }] });
    }
  }, [user, phase, rootNav]);

  const handleGetStarted = useCallback(async () => {
    if (!inviteInfo) return;

    setIsSubmitting(true);
    setOtpError(null);

    try {
      const baseUrl = process.env.EXPO_PUBLIC_API_BASE_URL;
      const res = await fetch(`${baseUrl}/beta-invites/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) throw new Error("Failed to start invite. Try again.");
      const json = await res.json();
      setMethodId(json.data.methodId);
      setPhoneLast4(json.data.phoneLast4 ?? "");
      setPhase("otp");
    } catch (err) {
      console.error("Beta invite start error:", err);
      setOtpError(
        err instanceof Error ? err.message : "Failed to send code. Try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [inviteInfo, code]);

  const handleVerifyCode = useCallback(
    async (otpCode: string) => {
      if (otpCode.length !== 6) return;

      setIsSubmitting(true);
      setOtpError(null);

      try {
        await stytch.otps.authenticate(otpCode, methodId, {
          session_duration_minutes: 60 * 24 * 30,
        });
      } catch (err) {
        console.error("Beta invite OTP verify error:", err);
        // Stytch's message is developer text ("[401] otp_code_not_found ...");
        // a wrong or expired code reads as one, anything else as generic.
        const errorType =
          err instanceof Error && "error_type" in err ? err.error_type : null;
        setOtpError(
          errorType === "otp_code_not_found" ||
            errorType === "unable_to_auth_otp_code"
            ? "Invalid code. Please try again."
            : "Something went wrong. Please try again.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [stytch, methodId],
  );

  const handleResendCode = useCallback(async () => {
    if (!code) return;

    setOtpError(null);
    setIsSubmitting(true);

    try {
      const baseUrl = process.env.EXPO_PUBLIC_API_BASE_URL;
      const res = await fetch(`${baseUrl}/beta-invites/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) throw new Error("Failed to resend code. Try again.");
      const json = await res.json();
      setMethodId(json.data.methodId);
      setPhoneLast4(json.data.phoneLast4 ?? "");
    } catch (err) {
      console.error("Beta invite resend error:", err);
      setOtpError("Failed to resend code. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [code]);

  const maskedPhone = phoneLast4 ? `•••• ${phoneLast4}` : "";

  let content: React.ReactNode;

  if (phase === "validating") {
    content = (
      <LoadingState
        testID="screen-loading"
        appearance="dark"
        style={{ paddingVertical: 20 }}
      />
    );
  } else if (phase === "error") {
    content = (
      <View style={{ alignItems: "center" }}>
        <Animated.Text
          className="font-dm-sans"
          style={{
            fontSize: 24,
            fontWeight: "700",
            color: DARK.text,
            textAlign: "center",
            marginBottom: 8,
            opacity: titleOpacity,
          }}
        >
          Invalid invite
        </Animated.Text>
        <Animated.Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: DARK.textMid,
            textAlign: "center",
            lineHeight: 21,
            opacity: bodyOpacity,
          }}
        >
          {validationError ?? "This invite link isn't valid or has expired."}
        </Animated.Text>
      </View>
    );
  } else if (phase === "welcome") {
    content = (
      <View style={{ alignItems: "center" }}>
        {/* Eyebrow */}
        <Animated.Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "600",
            letterSpacing: 1.5,
            textTransform: "uppercase",
            color: DARK.textFaint,
            marginBottom: 28,
            opacity: titleOpacity,
            transform: [{ translateY: titleTranslateY }],
          }}
        >
          {t("onboarding.beta_invite.private_access")}
        </Animated.Text>

        {/* Big welcome — Tobias font + gradient text on web */}
        <Animated.Text
          style={
            {
              fontSize: 42,
              fontFamily: "Tobias-Thin",
              color: DARK.text,
              textAlign: "center",
              lineHeight: 50,
              letterSpacing: -1,
              marginBottom: 28,
              opacity: titleOpacity,
              transform: [{ translateY: titleTranslateY }],
              ...(Platform.OS === "web" && {
                backgroundImage:
                  "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.6) 40%, rgba(212,165,116,0.9) 60%, rgba(255,255,255,0.8) 80%, rgba(255,255,255,1) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                backgroundSize: "200% 200%",
                animation: "inviteTitleShimmer 6s ease-in-out infinite",
              }),
            } as any // eslint-disable-line @typescript-eslint/no-explicit-any
          }
        >
          Welcome, {inviteInfo?.name}
        </Animated.Text>

        {/* Body — invite text + Atlal contact inline */}
        <Animated.Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: DARK.textMid,
            textAlign: "center",
            lineHeight: 26,
            marginBottom: 48,
            opacity: bodyOpacity,
            transform: [{ translateY: bodyTranslateY }],
          }}
        >
          {
            "You've been personally invited by Atlal to get access to Louise, feel free to text her any time with any issues or feedback."
          }
        </Animated.Text>

        {/* CTA button — shimmer border */}
        <Animated.View
          style={{
            width: "100%",
            opacity: ctaOpacity,
            transform: [{ translateY: ctaTranslateY }],
          }}
        >
          <GlassButton
            variant="shimmer"
            label="Get started"
            onPress={handleGetStarted}
            disabled={isSubmitting}
            loading={isSubmitting}
            trailing={
              <ArrowRight
                size={16}
                color={semanticColors.onDark.text.primary}
                strokeWidth={2}
              />
            }
            style={{ width: "100%" }}
          />
        </Animated.View>

        {otpError && (
          <FieldMessage onDark centered style={{ marginTop: 10 }}>
            {otpError}
          </FieldMessage>
        )}
      </View>
    );
  } else {
    // OTP phase
    content = (
      <View style={{ alignItems: "center" }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 24,
            fontWeight: "700",
            color: DARK.text,
            textAlign: "center",
            marginBottom: 8,
          }}
        >
          Enter your code
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: DARK.textMid,
            textAlign: "center",
            lineHeight: 21,
            marginBottom: 24,
          }}
        >
          We sent a 6-digit code to {maskedPhone}
        </Text>

        <OTPInput length={6} onComplete={handleVerifyCode} appearance="dark" />

        {otpError && (
          <FieldMessage
            testID="beta-otp-error"
            onDark
            centered
            style={{ marginTop: 12 }}
          >
            {otpError}
          </FieldMessage>
        )}

        {isSubmitting && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: DARK.textFaint,
              textAlign: "center",
              marginTop: 12,
            }}
          >
            Verifying...
          </Text>
        )}

        <View
          style={{
            flexDirection: "row",
            justifyContent: "center",
            gap: 20,
            marginTop: 24,
          }}
        >
          <TextActionButton
            label="Resend code"
            action="beta_invite.resend_code"
            onPress={() => void handleResendCode()}
            disabled={isSubmitting}
            tone="onDark"
            size="sm"
            icon={RotateCcw}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      {/* Dark fabric background with drift */}
      <Animated.Image
        source={require("../assets/images/beta-invite-bg.avif")}
        style={[
          StyleSheet.absoluteFill,
          {
            width: "120%",
            height: "120%",
            left: "-10%",
            top: "-10%",
            transform: [
              { translateX: bgTranslateX },
              { translateY: bgTranslateY },
              { scale: bgScale },
            ],
          },
        ]}
        resizeMode="cover"
      />

      {/* Dark overlay */}
      <View
        style={[
          StyleSheet.absoluteFill,
          { backgroundColor: semanticColors.overlay.lightScrim },
        ]}
      />

      <SafeAreaView style={{ flex: 1 }}>
        <View style={[styles.wrapper, isCompact && styles.wrapperCompact]}>
          {/* Orb — floats above the card, animates first */}
          <Animated.View
            style={{
              alignItems: "center",
              marginBottom: -48,
              zIndex: 10,
              opacity: orbOpacity,
              transform: [{ scale: orbScale }],
            }}
          >
            <GradientOrb size={96} />
          </Animated.View>

          <InviteGlassCard
            style={{
              opacity: phase === "validating" ? 1 : cardOpacity,
              transform: [
                {
                  translateY: phase === "validating" ? 0 : cardTranslateY,
                },
              ],
            }}
          >
            {content}
          </InviteGlassCard>
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    backgroundColor: "#0A0A0A",
  },
  wrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  wrapperCompact: {
    justifyContent: "flex-end",
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
});
